// The Meta (Facebook) Pixel, as both sites use it (#780): how it is started
// while a grant is in force, what it may be told, and which cookies a Decline
// removes. The runtimes are ./metaMeasurement.js on the marketing site and
// src/lib/metaPixel.js in the app; this file is what they share, and it lives
// beside ./adTag.js for the reason that file gives: the marketing bundle may
// not import from src/, and the app may import from here. It imports nothing
// but ./adTag.js, which imports nothing but ./consent.js.
//
// ── What fires, and when ──────────────────────────────────────────────────
//
// Pixel 2313751926045409 (public; not a secret) on every page of both sites,
// under the same one answer as the Google Ads tag and the app's analytics:
// on unless the visitor chose "Decline" or "Cookie settings", and a Global
// Privacy Control signal with no answer on record is an opt-out
// (`getConsent`, ./consent.js). The events, each with a deterministic
// `eventID` so that the Conversions API (#781) can send the same events
// from the server and Meta removes the duplicates:
//
//   PageView          every page; in the app, every route change too.
//   StartTrial        an account was made (a sign-up begins the 30-day
//                     trial, no card), where the Google Ads sign-up
//                     conversion is counted: `starttrial_<sha256 of uid>`.
//   InitiateCheckout  the teacher opens Stripe Checkout; no value. A fresh
//                     random id, handed to `createCheckoutSession` too.
//   AddPaymentInfo    Checkout came back complete, where the Google Ads paid
//                     conversion is counted: in the trial a card goes on
//                     file and nothing is charged, so no value and no
//                     currency. `addpaymentinfo_<checkout session id>`.
//
// The two deterministic ids and the advanced-matching hashes are
// src/domain/metaEvents.js, where the server's mirror can make the same ones;
// this file may not import from src/ (scripts/check-marketing-bundle.mjs), and
// the marketing site sends neither event.
//
// Never CompleteRegistration, Subscribe or Purchase from the browser: no
// money moves in the trial, and Purchase is the server's, on the first real
// charge.
// Advanced matching is the SHA-256 hex of the trimmed, lowercased email
// (`em`) and of the Firebase uid (`external_id`), sent with the sign-up; the
// plain email never leaves the page. A PageView lets Meta build audiences and
// retarget from it — for Meta only; the Google tag's `CONSENT_MODE` is
// unchanged.
//
// ── What it is never allowed to do ────────────────────────────────────────
//
// `autoConfig` is switched off before `init`. Left on, Meta's automatic event
// setup reads button text and page metadata and sends them as events — on the
// app's screens, students' and families' names. No `<noscript>` image
// fallback, on either site: an image fires with no script to read the
// answer, so it would count a visitor who declined. And `disablePushState`
// is set, so the pixel does not send PageViews of its own when the app's
// router changes the address; the app sends them itself, once the address
// carries nothing personal (src/lib/metaPixel.js `addressIsClean`).
// (Reasoned from Meta's pixel documentation, not observed; the Meta Pixel
// Helper shows what actually fires.)

import { cookieDeletions } from './adTag.js';

export const META_PIXEL_ID = '2313751926045409';
export const META_PIXEL_URL = 'https://connect.facebook.net/en_US/fbevents.js';

/** A pixel id: digits only. Anything else is not configured. */
export const isMetaPixelId = (id) => typeof id === 'string' && /^\d{10,20}$/.test(id);

/** The events the browser sends, and no others. */
export const META_EVENTS = Object.freeze({
  pageView: 'PageView',
  startTrial: 'StartTrial',
  initiateCheckout: 'InitiateCheckout',
  addPaymentInfo: 'AddPaymentInfo',
});

/** A fresh event id, for a PageView or an InitiateCheckout. */
export function freshEventId(prefix) {
  let id = null;
  try {
    id = globalThis.crypto?.randomUUID?.() ?? null;
  } catch {
    id = null;
  }
  if (!id) id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}_${id}`;
}

/**
 * Start the pixel for `id`: Meta's own loader stub, then autoConfig off, then
 * `init`, then the script. Returns the `fbq` function; calls made on it
 * before fbevents.js arrives are queued by the stub. Nothing is fetched or set
 * until this is called, and it is only called while a grant is in force.
 */
export function startMetaPixel({ id, win = window, doc = document }) {
  if (!win.fbq) {
    // Meta's base code, written out: a function that queues its calls until
    // fbevents.js supplies `callMethod`. `arguments` rather than a rest
    // parameter, because fbevents.js reads the queue as argument lists.
    const fbq = function fbq() {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    if (!win._fbq) win._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    win.fbq = fbq;
  }
  const fbq = win.fbq;
  // Our own PageViews only: none from the pixel's history listener.
  fbq.disablePushState = true;
  fbq.allowDuplicatePageViews = true;
  fbq('set', 'autoConfig', false, id);
  fbq('init', id);
  const script = doc.createElement('script');
  script.async = true;
  script.src = META_PIXEL_URL;
  doc.head.appendChild(script);
  return fbq;
}

/** A started pixel, told the answer changed in this page. */
export function updateMetaConsent(fbq, choice) {
  fbq('consent', choice === 'granted' ? 'grant' : 'revoke');
}

/** One event, with nothing but the parameters given and its `eventID`. */
export function trackMeta(fbq, name, params, eventID) {
  fbq('track', name, { ...(params || {}) }, { eventID });
}

// ── Withdrawing: the cookies to remove ────────────────────────────────────

// `_fbp`, the pixel's own browser id, and `_fbc`, the click id it keeps from
// an ad's `fbclid`. fbevents.js sets both on the widest domain the browser
// accepts, so they are removed host-only and on every parent domain, as the
// Google ones are (./adTag.js `cookieDeletions`).
const META_COOKIE = /^_fb[cp]$/;

/** The names of the Meta cookies in a `document.cookie` string. */
export function metaCookieNames(cookieHeader) {
  const names = new Set();
  for (const part of String(cookieHeader || '').split(';')) {
    const eq = part.indexOf('=');
    const name = (eq === -1 ? part : part.slice(0, eq)).trim();
    if (META_COOKIE.test(name)) names.add(name);
  }
  return [...names];
}

/** The assignments that remove the Meta cookies present. */
export const metaCookieDeletions = (cookieHeader, hostname) => cookieDeletions(metaCookieNames(cookieHeader), hostname);

/** Remove the Meta cookies from `doc`. Returns whether there were any. Never throws. */
export function clearMetaStorage({ doc = document, win = window } = {}) {
  try {
    const deletions = metaCookieDeletions(doc.cookie, win.location?.hostname);
    for (const c of deletions) doc.cookie = c;
    return deletions.length > 0;
  } catch {
    return false;
  }
}
